import React from 'react';
import { Box, Typography, ButtonBase, alpha, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Briefcase,
  ShoppingBag,
  PhoneCall,
  Menu,
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Dashboard', icon: TrendingUp, path: '/dashboard' },
  { label: 'Businesses', icon: Briefcase, path: '/businesses' },
  { label: 'Products', icon: ShoppingBag, path: '/products' },
  { label: 'Follow-ups', icon: PhoneCall, path: '/follow-ups' },
];

/**
 * MobileBottomNav - Dedicated native-feeling navigation bar for mobile PWA viewports.
 * Displays only on xs/sm screens (hidden on md+ desktops).
 * Fully respects iOS/Android bottom safe-area gesture insets.
 */
const MobileBottomNav = ({ onOpenMenu }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isMoreActive = !NAV_ITEMS.some((item) => location.pathname === item.path);

  return (
    <Box
      component="nav"
      aria-label="Mobile Bottom Navigation"
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (t) => t.zIndex.appBar,
        height: 'calc(60px + env(safe-area-inset-bottom, 0px))',
        pb: 'env(safe-area-inset-bottom, 0px)',
        bgcolor: alpha(theme.palette.background.paper, 0.94),
        backdropFilter: 'saturate(180%) blur(16px)',
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -8px 24px rgba(0, 0, 0, 0.4)'
          : '0 -4px 20px rgba(15, 23, 42, 0.06)',
        alignItems: 'center',
        justifyContent: 'space-around',
        px: 1,
      }}
    >
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;

        return (
          <ButtonBase
            key={item.path}
            onClick={() => navigate(item.path)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            sx={{
              flex: 1,
              py: 0.75,
              px: 0.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.4,
              borderRadius: 2,
              position: 'relative',
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:active': {
                transform: 'scale(0.92)',
              },
            }}
          >
            {/* Top pill active indicator */}
            {isActive && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  width: 24,
                  height: 3,
                  borderRadius: '0 0 3px 3px',
                  bgcolor: theme.palette.primary.main,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.6)}`,
                }}
              />
            )}
            <Box
              sx={{
                width: 32,
                height: 28,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                transition: 'background-color 0.2s',
              }}
            >
              <Icon size={20} strokeWidth={isActive ? 2.4 : 1.9} />
            </Box>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: isActive ? 700 : 500,
                lineHeight: 1,
                letterSpacing: '-0.01em',
              }}
              noWrap
            >
              {item.label}
            </Typography>
          </ButtonBase>
        );
      })}

      {/* Menu / All Sections trigger */}
      <ButtonBase
        onClick={onOpenMenu}
        aria-label="Open full menu"
        sx={{
          flex: 1,
          py: 0.75,
          px: 0.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.4,
          borderRadius: 2,
          position: 'relative',
          color: isMoreActive ? theme.palette.primary.main : theme.palette.text.secondary,
          transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:active': {
            transform: 'scale(0.92)',
          },
        }}
      >
        {isMoreActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              width: 24,
              height: 3,
              borderRadius: '0 0 3px 3px',
              bgcolor: theme.palette.primary.main,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.6)}`,
            }}
          />
        )}
        <Box
          sx={{
            width: 32,
            height: 28,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isMoreActive ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            transition: 'background-color 0.2s',
          }}
        >
          <Menu size={20} strokeWidth={isMoreActive ? 2.4 : 1.9} />
        </Box>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: isMoreActive ? 700 : 500,
            lineHeight: 1,
            letterSpacing: '-0.01em',
          }}
          noWrap
        >
          Menu
        </Typography>
      </ButtonBase>
    </Box>
  );
};

export default MobileBottomNav;

